import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useStateControllers, useStateSelector } from '../../stateManager';
import { stateKeys } from '../../constants';

const DisplayedContext = createContext();

const emptyArr = [];

const selectFilterValue = (state) => state[stateKeys.filterInfo];
const selectPage = (state) => state[stateKeys.page];
const selectPageSize = (state) => state[stateKeys.pageSize];

export const useDisplayedData = () => useContext(DisplayedContext);

export const DataPreparationProvider = ({
  children,
  getFilteredData,
  data,
  rowKey,
}) => {
  const getFilteredRef = useRef(getFilteredData);
  getFilteredRef.current = getFilteredData;

  const filterValue = useStateSelector(selectFilterValue);
  const page = useStateSelector(selectPage);

  const pageSize = useStateSelector(selectPageSize);

  const { setValue } = useStateControllers();

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) {
      return emptyArr;
    }

    if (
      !filterValue ||
      (typeof filterValue === 'object' && Object.keys(filterValue).length === 0)
    ) {
      return data;
    }

    return typeof getFilteredRef.current === 'function'
      ? getFilteredRef.current(data, filterValue)
      : data;
  }, [data, filterValue]);

  const totalItems = filtered.length;

  useEffect(() => {
    setValue(stateKeys.totalItems, totalItems);
  }, [setValue, totalItems]);

  useEffect(() => {
    const set = new Set();
    filtered.forEach((item) => {
      set.add(item[rowKey]);
    });
    setValue(stateKeys.preparedDataIdsSet, set);
  }, [filtered, setValue, rowKey]);

  const displayed = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  return (
    <DisplayedContext.Provider value={displayed}>
      {children}
    </DisplayedContext.Provider>
  );
};
